import type {
  ClientAdminsUser,
  GetApiV1ThreeLetterCodeClientAdminsPartnerFeatureFlags200,
} from '@fukuri-admins/apis/client/gen/model';
import type { ActionTableSortParamsType } from '~/types/table/ActionTableSortParamsType';
import { convertDateTime } from '~/utils/dateTime';
import { isApiIntegration } from '~/utils/isApiIntegration';
import type { PendingUserPresentation } from '../../presentations';

type PresentationProps = React.ComponentProps<typeof PendingUserPresentation>;

type Args = {
  users: ClientAdminsUser[];
  sortParams: ActionTableSortParamsType;
  totalCount: number;
  totalPendingCount: number;
  partnerFeatureFlagData?: GetApiV1ThreeLetterCodeClientAdminsPartnerFeatureFlags200;
};

type IConvertToPresentationProps = (args: Args) => {
  pendingUsers: PresentationProps['pendingUsers'];
  pendingRate: number;
  disableRowNavigation: boolean;
};

const compare = (a: string | number | undefined, b: string | number | undefined): number => {
  if (a === undefined && b === undefined) return 0;
  if (a === undefined) return -1;
  if (b === undefined) return 1;
  return a < b ? -1 : a > b ? 1 : 0;
};

/** ソート列に対応する生の値を取り出す（日時は整形前の数値で比較する） */
const getSortValue = (user: ClientAdminsUser, column: string): string | number | undefined => {
  switch (column) {
    case 'full_name':
      return user.full_name;
    case 'email':
      return user.email;
    case 'created_at':
      return user.created_at;
    case 'account_information_sent_at':
      return user.account_information_sent_at;
    default:
      return undefined;
  }
};

/**
 * API のユーザー一覧を Presentation の行データへ変換する。
 *
 * ソート → 表示用整形 の順で実施する。
 * - sortParams.column が空のときは入力順のまま
 * - 日時列は整形前の数値で比較する（整形後の文字列ではなく）
 * - isAsc は「反転フラグ」（true で降順 / false で昇順）
 * - no は整形後（ソート済み）の並び順に対する 1 始まりの連番
 */
export const convertToPresentationProps: IConvertToPresentationProps = ({
  users,
  sortParams,
  totalCount,
  totalPendingCount,
  partnerFeatureFlagData,
}) => {
  const pendingRate = totalCount > 0 ? Math.round((totalPendingCount / totalCount) * 100) : 0;
  const disableRowNavigation = isApiIntegration(partnerFeatureFlagData?.partner_feature_flags);
  const sortedUsers = sortParams.column
    ? [...users].sort((a, b) => {
        const result = compare(
          getSortValue(a, sortParams.column),
          getSortValue(b, sortParams.column)
        );
        return sortParams.isAsc ? -result : result;
      })
    : users;

  return {
    pendingRate,
    disableRowNavigation,
    pendingUsers: sortedUsers.map((user, index) => ({
      no: index + 1,
      id: String(user.id),
      full_name: user.full_name,
      email: user.email,
      created_at: convertDateTime(user.created_at),
      account_information_sent_at:
        user.account_information_sent_at === 0
          ? convertDateTime(user.created_at)
          : convertDateTime(user.account_information_sent_at),
    })),
  };
};
